import type {
  Block,
  DigestReport,
  OverlayItem,
  SearchContentType,
  SearchStatus,
} from '@almanac/contracts';
import { effectiveOverlayStatus } from '@almanac/contracts';
import type { DigestSummary } from '../api/client';

export interface SearchFilters {
  sources: string[];
  sections: string[];
  statuses: SearchStatus[];
  contentTypes: SearchContentType[];
  dateFrom: string | null;
  dateTo: string | null;
}

export const ORIGINAL_BLOCK_INDEX = '__almanacOriginalBlockIndex' as const;
type IndexedBlock = Block & { [ORIGINAL_BLOCK_INDEX]: number };

export const EMPTY_SEARCH_FILTERS: SearchFilters = {
  sources: [],
  sections: [],
  statuses: [],
  contentTypes: [],
  dateFrom: null,
  dateTo: null,
};

function normalized(value: string): string {
  return value.toLocaleLowerCase('en-US').replace(/\s+/gu, ' ').trim();
}

/** Pure banded ranking: exact/prefix > word boundary > substring > subsequence. */
export function rankCandidate(query: string, label: string, keywords = ''): number | null {
  const needle = normalized(query);
  if (!needle) return 0;
  const haystack = normalized(`${label} ${keywords}`);
  if (haystack === needle) return 1_000;
  if (haystack.startsWith(needle)) return 900 - Math.min(haystack.length - needle.length, 100);

  const boundary = haystack.search(new RegExp(`(?:^|\\s)${escapeRegExp(needle)}`, 'u'));
  if (boundary >= 0) return 700 - Math.min(boundary, 100);

  const substring = haystack.indexOf(needle);
  if (substring >= 0) return 500 - Math.min(substring, 100);

  let cursor = 0;
  for (const character of haystack) {
    if (character === needle[cursor]) cursor += 1;
    if (cursor === needle.length) return 250 - Math.min(haystack.length - needle.length, 100);
  }
  return null;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&');
}

function dateMatches(date: string, filters: SearchFilters): boolean {
  if (filters.dateFrom && date < filters.dateFrom) return false;
  if (filters.dateTo && date > filters.dateTo) return false;
  return true;
}

export function filterDigestSummaries(
  digests: DigestSummary[],
  query: string,
  filters: SearchFilters,
): DigestSummary[] {
  return digests.filter((digest) => {
    if (!dateMatches(digest.date, filters)) return false;
    if (
      filters.sources.length > 0 &&
      !filters.sources.some((source) => digest.sources.includes(source))
    ) {
      return false;
    }
    if (!query.trim()) return true;
    return rankCandidate(query, digest.title ?? digest.date, digest.sources.join(' ')) !== null;
  });
}

function blockContentType(block: Block): SearchContentType {
  return block.type;
}

function blockText(block: Block): string {
  return JSON.stringify(block);
}

function itemMatches(
  item: Extract<Block, { type: 'item_list' }>['items'][number],
  query: string,
  filters: SearchFilters,
  overlay: Record<string, OverlayItem>,
  sectionMatchesSource: boolean,
  hideAddressed: boolean,
): boolean {
  if (filters.contentTypes.length > 0 && !filters.contentTypes.includes('item')) return false;
  if (
    filters.sources.length > 0 &&
    !sectionMatchesSource &&
    (!item.source_type || !filters.sources.includes(item.source_type.toLocaleLowerCase('en-US')))
  ) {
    return false;
  }
  const effectiveId = item.item_id ?? item.mirrors_item_id;
  const overlayItem = effectiveId ? overlay[effectiveId] : undefined;
  const status = effectiveOverlayStatus(overlayItem);
  if (hideAddressed && status !== 'active') return false;
  if (filters.statuses.length > 0 && !filters.statuses.includes(status)) return false;
  if (!query.trim()) return true;
  return (
    rankCandidate(
      query,
      item.title,
      [item.detail, item.meta, item.src, item.source_ref, item.semantic_slot]
        .filter(Boolean)
        .join(' '),
    ) !== null
  );
}

function filterBlock(
  block: Block,
  query: string,
  filters: SearchFilters,
  overlay: Record<string, OverlayItem>,
  sectionId: string,
  sectionTitle: string,
  hideAddressed: boolean,
): Block | null {
  const sectionTerms = normalized(`${sectionId} ${sectionTitle}`).split(' ');
  const sectionMatchesSource = filters.sources.some((source) => sectionTerms.includes(source));
  if (block.type === 'item_list') {
    const blockTypeAllowed =
      filters.contentTypes.length === 0 || filters.contentTypes.includes('item_list');
    const blockMatches =
      blockTypeAllowed &&
      (filters.sources.length === 0 || sectionMatchesSource) &&
      filters.statuses.length === 0 &&
      (!query.trim() || rankCandidate(query, block.title ?? 'Items', block.note ?? '') !== null);
    if (blockMatches && !hideAddressed) return block;

    const items = block.items.filter((item) =>
      itemMatches(
        item,
        blockMatches ? '' : query,
        filters,
        overlay,
        sectionMatchesSource,
        hideAddressed,
      ),
    );
    if (items.length === 0) return null;
    return { ...block, items };
  }

  if ((filters.sources.length > 0 && !sectionMatchesSource) || filters.statuses.length > 0) {
    return null;
  }
  if (filters.contentTypes.length > 0 && !filters.contentTypes.includes(blockContentType(block))) {
    return null;
  }
  if (query.trim() && rankCandidate(query, block.type, blockText(block)) === null) return null;
  return block;
}

export function filterDigestReport(
  report: DigestReport,
  overlay: Record<string, OverlayItem>,
  query: string,
  filters: SearchFilters,
): DigestReport | null {
  if (!dateMatches(report.date, filters)) return null;
  const hideAddressed = filters.statuses.length === 0;

  const sections = Object.fromEntries(
    Object.entries(report.sections).flatMap(([sectionId, section]) => {
      if (filters.sections.length > 0 && !filters.sections.includes(sectionId)) return [];
      const blocks = section.blocks
        .map((block, originalBlockIndex) => {
          const filtered = filterBlock(
            block,
            query,
            filters,
            overlay,
            sectionId,
            section.title,
            hideAddressed,
          );
          return filtered
            ? ({ ...filtered, [ORIGINAL_BLOCK_INDEX]: originalBlockIndex } as IndexedBlock)
            : null;
        })
        .filter((block): block is IndexedBlock => block !== null);
      if (blocks.length === 0) return [];
      return [[sectionId, { ...section, blocks }]];
    }),
  );
  if (Object.keys(sections).length === 0) return null;
  return { ...report, sections };
}

export function activeFilterCount(filters: SearchFilters): number {
  return (
    filters.sources.length +
    filters.sections.length +
    filters.statuses.length +
    filters.contentTypes.length +
    Number(Boolean(filters.dateFrom)) +
    Number(Boolean(filters.dateTo))
  );
}
